import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem,
  Checkbox, Chip, Tabs, Tab, Tooltip, Alert, IconButton, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  WorkspacePremium as CompletionIcon,
  ExitToApp as MoveIcon,
  Visibility as BrowseIcon,
  Receipt as BillIcon,
  Undo as RestoreIcon,
  Search as SearchIcon,
  PlayArrow as RunIcon,
  CheckCircle as DoneIcon,
  Warning as WarnIcon,
  Refresh as ReloadIcon,
  History as BackfillIcon,
  AutoFixHigh as InferIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// Class Completion / Alumni — bulk move a class to alumni at the end of
// the academic year, browse alumni by cohort year, post one-off cohort
// bills (transcript fee, late billing) and reverse moves made by mistake.
//
// All four tabs share the same Card chrome. State lives at the page
// level only when it needs to be shared across tabs (the years list is
// the obvious one — moves and bills both invalidate it).
export default function CompletionPage() {
  const [tab, setTab] = useState(0);
  const [years, setYears] = useState([]);
  const [defaultYear, setDefaultYear] = useState('');

  const refreshYears = useCallback(async () => {
    try {
      const { data } = await API.get('/completion/years');
      setYears(data.years || []);
    } catch (e) { /* swallow — empty state */ }
  }, []);

  useEffect(() => { refreshYears(); }, [refreshYears]);
  useEffect(() => {
    API.get('/completion/active-classes')
      .then(r => setDefaultYear(r.data?.default_completion_year || ''))
      .catch(() => {});
  }, []);

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <ModuleHeader
        icon={<CompletionIcon />}
        title="Class Completion / Alumni"
        subtitle="Graduate a class to Alumni, browse alumni by completion year, post one-off cohort bills (transcript, late billing) and reverse mistaken moves."
      />
      <Card sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 46, fontSize: 13.5 } }}>
          <Tab icon={<MoveIcon sx={{ fontSize: 18 }} />}    iconPosition="start" label="Move to Alumni" />
          <Tab icon={<BrowseIcon sx={{ fontSize: 18 }} />}  iconPosition="start" label="Browse Cohort" />
          <Tab icon={<BillIcon sx={{ fontSize: 18 }} />}    iconPosition="start" label="Bill a Cohort" />
          <Tab icon={<RestoreIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Restore to Active" />
          <Tab icon={<BackfillIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Back-fill Year" />
        </Tabs>
      </Card>
      {tab === 0 && <MoveTab     defaultYear={defaultYear} onMoved={refreshYears} />}
      {tab === 1 && <BrowseTab   years={years} onRefresh={refreshYears} />}
      {tab === 2 && <BillTab     years={years} />}
      {tab === 3 && <RestoreTab  years={years} onRestored={refreshYears} />}
      {tab === 4 && <BackfillTab onAssigned={refreshYears} />}
    </Box>
  );
}

// ── Tab 1: Move a class to Alumni ─────────────────────────────
function MoveTab({ defaultYear, onMoved }) {
  const [classes, setClasses]       = useState([]);
  const [cla, setCla]               = useState('');
  const [year, setYear]             = useState(defaultYear);
  const [students, setStudents]     = useState([]);
  const [picked, setPicked]         = useState(new Set());
  const [loading, setLoading]       = useState(false);
  const [busy, setBusy]             = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => { setYear(defaultYear); }, [defaultYear]);

  useEffect(() => {
    API.get('/completion/active-classes')
      .then(r => setClasses(r.data?.classes || []))
      .catch(() => {});
  }, []);

  // Reload roster whenever the class changes.
  useEffect(() => {
    setStudents([]); setPicked(new Set());
    if (!cla) return;
    setLoading(true);
    API.get('/completion/preview', { params: { cla } })
      .then(r => {
        const list = r.data?.students || [];
        setStudents(list);
        setPicked(new Set(list.map(s => s.ind)));
      })
      .catch(e => swal.error(swal.pickError(e, 'Could not load roster')))
      .finally(() => setLoading(false));
  }, [cla]);

  const togglePick = (ind) => {
    setPicked(prev => {
      const next = new Set(prev);
      next.has(ind) ? next.delete(ind) : next.add(ind);
      return next;
    });
  };
  const pickAll = () => setPicked(new Set(students.map(s => s.ind)));
  const pickNone = () => setPicked(new Set());

  const handleMove = async () => {
    if (!cla)  return swal.warning('Pick a class first');
    if (!year) return swal.warning('Enter a completion year (e.g. 2025/2026)');
    if (picked.size === 0) return swal.warning('Pick at least one student');
    setBusy(true);
    try {
      const { data } = await API.post('/completion/move', {
        cla,
        completion_year: year,
        student_inds: [...picked],
      });
      swal.success(data.message || `Moved ${data.moved_count} student(s)`);
      setConfirmOpen(false);
      // Refresh the classes picker so the just-moved class shows the
      // remaining headcount (or disappears if it was the whole class).
      API.get('/completion/active-classes')
        .then(r => setClasses(r.data?.classes || []));
      setStudents([]); setPicked(new Set()); setCla('');
      onMoved?.();
    } catch (e) {
      swal.error(swal.pickError(e, 'Move failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Pick the class that has completed and the completion year. The students
        you tick will move to <b>Alumni</b>; their current class is kept on
        record (<i>prev_calss</i>) so the Restore tab can put them back
        exactly where they came from if you change your mind.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField select fullWidth label="Class to graduate" value={cla}
            onChange={e => setCla(e.target.value)} size="small">
            <MenuItem value=""><i>Select class…</i></MenuItem>
            {classes.map(c => (
              <MenuItem key={c.cla} value={c.cla}>{c.cla} · {c.n} student(s)</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField fullWidth label="Completion year" value={year}
            onChange={e => setYear(e.target.value)} size="small"
            placeholder="e.g. 2025/2026" />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" startIcon={<RunIcon />}
            disabled={!cla || !year || picked.size === 0 || busy}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Move {picked.size} → Alumni
          </Button>
        </Grid>
      </Grid>

      {cla && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Button size="small" onClick={pickAll}   disabled={loading || students.length === 0}>Select all</Button>
          <Button size="small" onClick={pickNone}  disabled={loading || picked.size === 0}>Clear</Button>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" color="primary" label={`${picked.size} of ${students.length} selected`} />
        </Stack>
      )}

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && students.length > 0 && (
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>#</TableCell>
                <TableCell>Index</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Current Class</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((s, i) => (
                <TableRow key={s.ind} hover>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={picked.has(s.ind)}
                      onChange={() => togglePick(s.ind)} />
                  </TableCell>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.ind}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {`${s.firstname || ''} ${s.lastname || ''}`.trim().toUpperCase()}
                  </TableCell>
                  <TableCell>{s.gender || ''}</TableCell>
                  <TableCell>{s.calss}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarnIcon color="warning" /> Confirm move to Alumni
        </DialogTitle>
        <DialogContent>
          <Typography>
            Move <b>{picked.size}</b> student(s) from <b>{cla}</b> to Alumni
            cohort <b>{year}</b>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            They will no longer count as active students. You can bring them back
            from the Restore tab as long as none of their alumni-cohort bills
            have been paid.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleMove}
            disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <DoneIcon />}>
            {busy ? 'Moving…' : 'Yes, move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ── Tab 2: Browse a cohort ─────────────────────────────────────
function BrowseTab({ years, onRefresh }) {
  const [year, setYear]         = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [q, setQ]               = useState('');

  useEffect(() => {
    if (!year) { setStudents([]); return; }
    setLoading(true);
    API.get('/completion/cohort', { params: { year } })
      .then(r => setStudents(r.data?.students || []))
      .catch(e => swal.error(swal.pickError(e, 'Could not load cohort')))
      .finally(() => setLoading(false));
  }, [year]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return students;
    return students.filter(s =>
      (s.ind || '').toLowerCase().includes(needle)
      || `${s.firstname} ${s.lastname}`.toLowerCase().includes(needle)
      || (s.prev_calss || '').toLowerCase().includes(needle)
    );
  }, [students, q]);

  return (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField select fullWidth label="Completion year" value={year}
            onChange={e => setYear(e.target.value)} size="small">
            <MenuItem value=""><i>Pick a cohort…</i></MenuItem>
            {years.map(y => (
              <MenuItem key={y.year} value={y.year}>
                {y.year} — {y.n} alumnus/i
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField fullWidth size="small" placeholder="Search name, index or previous class"
            value={q} onChange={e => setQ(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }} />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh}><ReloadIcon /></IconButton>
          </Tooltip>
          {year && <Chip color="primary" size="small" label={`${filtered.length} shown`} />}
        </Grid>
      </Grid>

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && year && students.length === 0 && (
        <Alert severity="info">No alumni in this cohort yet.</Alert>
      )}

      {!loading && filtered.length > 0 && (
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Index</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Previous Class</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((s, i) => (
                <TableRow key={s.ind} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.ind}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {`${s.firstname || ''} ${s.lastname || ''}`.trim().toUpperCase()}
                  </TableCell>
                  <TableCell>{s.gender || ''}</TableCell>
                  <TableCell>{s.prev_calss || ''}</TableCell>
                  <TableCell>{s.completion_date ? String(s.completion_date).slice(0, 10) : ''}</TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 700,
                    color: Number(s.balance) > 0 ? '#b71c1c' : '#1b5e20',
                  }}>
                    {Number(s.balance || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

// ── Tab 3: Bill a cohort ──────────────────────────────────────
function BillTab({ years }) {
  const [year, setYear]         = useState('');
  const [account, setAccount]   = useState('');
  const [amount, setAmount]     = useState('');
  const [term, setTerm]         = useState('Term_1');
  const [narrative, setNarrative] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [bills, setBills]       = useState([]);
  const [busy, setBusy]         = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    API.get('/completion/income-types')
      .then(r => setAccounts(r.data?.accounts || []))
      .catch(() => {});
  }, []);

  const loadBills = useCallback(async (y) => {
    if (!y) { setBills([]); return; }
    try {
      const { data } = await API.get('/completion/cohort-bills', { params: { year: y } });
      setBills(data.bills || []);
    } catch (e) { setBills([]); }
  }, []);

  useEffect(() => { loadBills(year); }, [year, loadBills]);

  const handleBill = async () => {
    setBusy(true);
    try {
      const { data } = await API.post('/completion/bill', {
        completion_year: year, account, amount: Number(amount),
        term, narrative: narrative.trim() || account,
      });
      swal.success(data.message || 'Bills posted');
      setConfirmOpen(false);
      setAmount(''); setNarrative('');
      loadBills(year);
    } catch (e) {
      swal.error(swal.pickError(e, 'Billing failed'));
    } finally {
      setBusy(false);
    }
  };

  const yearMeta = years.find(y => y.year === year);
  const headcount = yearMeta?.n || 0;
  const total = (Number(amount) || 0) * headcount;
  const canBill = year && account && Number(amount) > 0 && term && headcount > 0;

  return (
    <Card sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Posts a one-off flat-amount bill against every alumnus in the cohort.
        Each row lands in the standard receivables tables (<i>admission</i> +
        <i>payment_detail</i>) tagged with a cohort bill id, so the Restore
        tab can wipe them atomically if no payment has come in yet.
        Re-running the same bill is safe — already-billed alumni are skipped.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField select fullWidth label="Cohort year" value={year}
            onChange={e => setYear(e.target.value)} size="small">
            <MenuItem value=""><i>Pick a year…</i></MenuItem>
            {years.map(y => (
              <MenuItem key={y.year} value={y.year}>{y.year} — {y.n} alumnus/i</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField select fullWidth label="Account / income type" value={account}
            onChange={e => setAccount(e.target.value)} size="small">
            <MenuItem value=""><i>Pick an account…</i></MenuItem>
            {accounts.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField fullWidth label="Amount per alumnus" value={amount}
            onChange={e => setAmount(e.target.value.replace(/[^\d.]/g, ''))} size="small"
            placeholder="e.g. 50" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField select fullWidth label="Term" value={term}
            onChange={e => setTerm(e.target.value)} size="small">
            <MenuItem value="Term_1">Term 1</MenuItem>
            <MenuItem value="Term_2">Term 2</MenuItem>
            <MenuItem value="Term_3">Term 3</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button variant="contained" startIcon={<BillIcon />}
            disabled={!canBill || busy} onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Bill cohort
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Narrative (shown on receipt)"
            value={narrative} onChange={e => setNarrative(e.target.value)}
            placeholder={account ? `Defaults to "${account}"` : 'Description shown on the bill'} />
        </Grid>
      </Grid>

      {year && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
            Cohort billing history — <b>{year}</b>
          </Typography>
          {bills.length === 0
            ? <Alert severity="info">No bills posted yet for this cohort.</Alert>
            : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Posted</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Narrative</TableCell>
                      <TableCell>Term</TableCell>
                      <TableCell align="right">Per student</TableCell>
                      <TableCell align="right">Billed</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bills.map(b => (
                      <TableRow key={b.bill_id} hover>
                        <TableCell>{b.dat ? String(b.dat).slice(0, 10) : ''}</TableCell>
                        <TableCell>{b.account}</TableCell>
                        <TableCell>{b.narrative}</TableCell>
                        <TableCell>{(b.term || '').replace(/_/g, ' ')}</TableCell>
                        <TableCell align="right">{Number(b.amount_per_student || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{b.billed}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {Number(b.total_billed || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          }
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarnIcon color="warning" /> Confirm cohort bill
        </DialogTitle>
        <DialogContent>
          <Typography>
            Bill <b>{headcount}</b> alumnus/i in cohort <b>{year}</b>
            &nbsp;<b>{Number(amount || 0).toFixed(2)}</b> each under <b>{account}</b>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Total exposure: <b>{total.toFixed(2)}</b>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Already-billed alumni (same account + narrative + year) are skipped automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleBill}
            disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <DoneIcon />}>
            {busy ? 'Posting…' : 'Post bills'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ── Tab 4: Restore to Active ──────────────────────────────────
function RestoreTab({ years, onRestored }) {
  const [year, setYear]         = useState('');
  const [students, setStudents] = useState([]);
  const [picked, setPicked]     = useState(new Set());
  const [loading, setLoading]   = useState(false);
  const [busy, setBusy]         = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setStudents([]); setPicked(new Set());
    if (!year) return;
    setLoading(true);
    API.get('/completion/cohort', { params: { year } })
      .then(r => setStudents(r.data?.students || []))
      .catch(e => swal.error(swal.pickError(e, 'Could not load cohort')))
      .finally(() => setLoading(false));
  }, [year]);

  const togglePick = (ind) => setPicked(prev => {
    const next = new Set(prev);
    next.has(ind) ? next.delete(ind) : next.add(ind);
    return next;
  });
  const pickAll  = () => setPicked(new Set(students.map(s => s.ind)));
  const pickNone = () => setPicked(new Set());

  const handleRestore = async () => {
    setBusy(true);
    try {
      const body = picked.size === students.length
        ? { completion_year: year }
        : { student_inds: [...picked] };
      const { data } = await API.post('/completion/restore', body);
      swal.success(data.message || `Restored ${data.restored} student(s)`);
      setConfirmOpen(false);
      setStudents([]); setPicked(new Set()); setYear('');
      onRestored?.();
    } catch (e) {
      swal.error(swal.pickError(e, 'Restore failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Restoring puts each student back into their <b>previous class</b> and
        wipes any unpaid alumni-cohort bills they were issued. Students whose
        alumni bill has already been paid are blocked — reverse the receipt
        in <b>Receipts Ledger &amp; Reversals</b> first, then restore.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField select fullWidth label="Cohort year" value={year}
            onChange={e => setYear(e.target.value)} size="small">
            <MenuItem value=""><i>Pick a cohort…</i></MenuItem>
            {years.map(y => (
              <MenuItem key={y.year} value={y.year}>{y.year} — {y.n} alumnus/i</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" color="warning" startIcon={<RestoreIcon />}
            disabled={!year || picked.size === 0 || busy}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Restore {picked.size} → Active
          </Button>
        </Grid>
      </Grid>

      {year && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Button size="small" onClick={pickAll}  disabled={loading || students.length === 0}>Select all</Button>
          <Button size="small" onClick={pickNone} disabled={loading || picked.size === 0}>Clear</Button>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" color="primary" label={`${picked.size} of ${students.length} selected`} />
        </Stack>
      )}

      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && students.length > 0 && (
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"></TableCell>
                <TableCell>#</TableCell>
                <TableCell>Index</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Previous Class</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((s, i) => (
                <TableRow key={s.ind} hover>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={picked.has(s.ind)}
                      onChange={() => togglePick(s.ind)} />
                  </TableCell>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.ind}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {`${s.firstname || ''} ${s.lastname || ''}`.trim().toUpperCase()}
                  </TableCell>
                  <TableCell>{s.prev_calss || ''}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {Number(s.balance || 0).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarnIcon color="warning" /> Confirm restore
        </DialogTitle>
        <DialogContent>
          <Typography>
            Restore <b>{picked.size}</b> student(s) from Alumni cohort <b>{year}</b>
            back to their previous class?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Unpaid alumni-cohort bills will be wiped. Students who have already
            paid against an alumni bill will block the restore — go to Receipts
            Ledger first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRestore}
            disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <DoneIcon />}>
            {busy ? 'Restoring…' : 'Yes, restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ── Tab 5: Back-fill year for existing alumni ─────────────────
// Walks every alumnus who has no completion_year stamped, infers a
// likely cohort year from their MAX `acc_yer` in admission/payment
// records, and groups them into accordion buckets so an admin can
// confirm a whole batch at once or override the year per group.
function BackfillTab({ onAssigned }) {
  const [buckets, setBuckets] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);
  // Per-bucket form state: { [year]: { override: '', picked: Set } }
  const [bucketState, setBucketState] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/completion/unassigned');
      const list = data.buckets || [];
      setBuckets(list);
      setTotal(data.total || 0);
      // Pre-fill picks: select every student in every bucket so a
      // confirm-and-go workflow takes one click per group.
      const init = {};
      for (const b of list) {
        const key = b.inferred_year || 'unknown';
        init[key] = {
          override: b.inferred_year || '',
          picked: new Set(b.students.map(s => s.ind)),
        };
      }
      setBucketState(init);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load unassigned alumni'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateBucket = (key, patch) => setBucketState(prev => ({
    ...prev, [key]: { ...prev[key], ...patch },
  }));

  const togglePick = (key, ind) => setBucketState(prev => {
    const cur = prev[key] || { override: '', picked: new Set() };
    const next = new Set(cur.picked);
    next.has(ind) ? next.delete(ind) : next.add(ind);
    return { ...prev, [key]: { ...cur, picked: next } };
  });

  const apply = async (bucket) => {
    const key = bucket.inferred_year || 'unknown';
    const state = bucketState[key] || {};
    const year = (state.override || '').trim();
    const inds = [...(state.picked || new Set())];
    if (!year)         { swal.warning('Type the completion year first (e.g. 2024/2025)'); return; }
    if (inds.length === 0) { swal.warning('Pick at least one student in this group'); return; }
    setBusy(true);
    try {
      const { data } = await API.post('/completion/assign-year', {
        completion_year: year,
        student_inds: inds,
      });
      swal.success(data.message || `Stamped ${data.assigned} alumnus/i`);
      onAssigned?.();
      load();
    } catch (e) {
      swal.error(swal.pickError(e, 'Assign failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ p: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }} icon={<InferIcon />}>
        Existing alumni in this database that don't yet carry a completion year
        are grouped below by the <b>last academic year</b> we see them in the
        admission / payment records — that's usually the year they completed.
        Confirm the year (or edit it) and apply per group. Students in the
        <b> "(unknown)" </b> bucket have no financial footprint to infer from —
        you'll need to type their year manually.
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip color={total ? 'warning' : 'success'}
          label={total ? `${total} alumnus/i still need a year` : 'All alumni have a year — nothing to do'} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload from database">
          <span><IconButton onClick={load} disabled={loading}><ReloadIcon /></IconButton></span>
        </Tooltip>
      </Stack>

      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

      {!loading && buckets.length === 0 && (
        <Alert severity="success">
          Every alumnus already carries a completion year. Nothing to back-fill.
        </Alert>
      )}

      {!loading && buckets.map((b) => {
        const key = b.inferred_year || 'unknown';
        const state = bucketState[key] || { override: '', picked: new Set() };
        const yearLabel = b.inferred_year || '(unknown — no financial footprint)';
        return (
          <Accordion key={key} defaultExpanded={buckets.length <= 3}
            sx={{ mb: 1, '&:before': { display: 'none' }, border: '1px solid #e0e0e0' }}>
            <AccordionSummary expandIcon={<ExpandIcon />}
              sx={{ bgcolor: b.inferred_year ? '#e8eaf6' : '#fff3e0' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                <Typography fontWeight={800} sx={{ color: b.inferred_year ? '#1a237e' : '#e65100' }}>
                  {yearLabel}
                </Typography>
                <Chip size="small" label={`${b.students.length} student(s)`} />
                <Box sx={{ flex: 1 }} />
                <Chip size="small" variant="outlined"
                  label={`${state.picked?.size || 0} selected`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField fullWidth size="small" label="Completion year"
                    value={state.override} placeholder="e.g. 2024/2025"
                    onChange={e => updateBucket(key, { override: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm="auto">
                  <Button size="small"
                    onClick={() => updateBucket(key, { picked: new Set(b.students.map(s => s.ind)) })}>
                    Select all
                  </Button>
                </Grid>
                <Grid item xs={6} sm="auto">
                  <Button size="small"
                    onClick={() => updateBucket(key, { picked: new Set() })}>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} sm sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" color="primary" startIcon={<DoneIcon />}
                    disabled={busy || !state.override || (state.picked?.size || 0) === 0}
                    onClick={() => apply(b)}
                    sx={{ textTransform: 'none', fontWeight: 800 }}>
                    Apply year to {state.picked?.size || 0} student(s)
                  </Button>
                </Grid>
              </Grid>

              <TableContainer sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>#</TableCell>
                      <TableCell>Index</TableCell>
                      <TableCell>Student Name</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Last class on file</TableCell>
                      <TableCell>Registered</TableCell>
                      <TableCell>Inferred year</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {b.students.map((s, i) => (
                      <TableRow key={s.ind || s.trans_code} hover>
                        <TableCell padding="checkbox">
                          <Checkbox size="small"
                            checked={state.picked?.has(s.ind) || false}
                            onChange={() => togglePick(key, s.ind)} />
                        </TableCell>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{s.ind}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {`${s.firstname || ''} ${s.lastname || ''}`.trim().toUpperCase()}
                        </TableCell>
                        <TableCell>{s.gender || ''}</TableCell>
                        <TableCell>{s.cur_class || s.class_alt || ''}</TableCell>
                        <TableCell>{s.re_dat || ''}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{s.inferred_year || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Card>
  );
}
